// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// cues.ts — every sound the game can make, declared in one place.
//
// This file exists because `sfx.play('mistyped')` returns SILENTLY. There is no error, no warning
// and no console line: the mixer looks the cue up, does not find it, and returns. A whole game can
// therefore ship with a green test suite and no sound at all. CUES is the declared list and
// tests/source-hygiene.test.ts greps every ping()/sfx.play() literal in src/ against it, so a typo
// is a failing build rather than an inaudible bug.
//
// A real-time game plays a LOT of these — a dozen shots a second in a big fight — so every patch is
// short and quiet, and main.ts rate-limits the combat cues. Pitch is used as a channel rather than
// adding patches: a shot is pitched by the firing unit's type, a kill by the dying unit's cost.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUES = [
  'select',
  'order',
  'build',
  'deny',
  'shot',
  'thud',
  'wreck',
  'claim',
  'slip',
  'warn',
  'win',
  'lose',
  'beat',
  'go',
] as const;

export const PATCHES: Record<string, Patch> = {
  /** A confirmed move/attack order. Soft, because it fires constantly. */
  order: { type: 'triangle', freq: [300, 460], dur: 0.06, gain: 0.14 },
  /** A unit rolls off the line. */
  build: { type: 'square', freq: [180, 420], dur: 0.16, gain: 0.18 },
  /** Not enough ore, or an illegal order — a flat, unmusical refusal. */
  deny: { type: 'square', freq: [160, 120], dur: 0.1, gain: 0.16 },
  /** A ranged shot. Pitched by unit type at the call site. */
  shot: { type: 'square', freq: [720, 430], dur: 0.05, gain: 0.1 },
  /** Melee contact. */
  thud: { type: 'sawtooth', freq: [200, 70], dur: 0.08, gain: 0.16, noise: true },
  /** A unit dies and leaves a wreck. Pitched down for the heavier types. */
  wreck: { type: 'sawtooth', freq: [260, 50], dur: 0.34, gain: 0.26, noise: true },
  /** A seam changes hands your way. */
  claim: { type: 'triangle', freq: [520, 880], dur: 0.22, gain: 0.22 },
  /** …and the other way. */
  slip: { type: 'triangle', freq: [700, 320], dur: 0.24, gain: 0.2 },
  /** The match clock is about to force the issue. */
  warn: { type: 'sine', freq: [440, 330], dur: 0.3, gain: 0.18 },
};
